import { supabase } from "../supabase";
import { safeNum, pickColor, escapeFilterValue, propertyLabel} from "./helpers";
import { pmError } from "./errors";
import { logAudit } from "./audit";
import { queueNotification } from "./notifications";
import { RENT_CHARGE_PREFIXES, pickLegacyNamedArAccount, nextTenantArSeq } from "./paymentRules";
import { syncOwnerAccruals, tenantRentChargeInMonth } from "./ownerRules";
import { BILLABLE_LEASE_STATUSES, isTenantBillable, recurringTenantSkipReason, pickTenantArAccount, monthBounds, arAlreadyBilledInMonth } from "./recurringRules";
import { RELEASED_DEPOSIT_STATUSES, depositReleaseKey, depositReleaseReference, depositDeductionReference, depositReleaseReferences, decideDepositRelease, depositReturnOfferable, planReleaseLegs, releasedDepositStatus, isKeyedReleaseRef, depositReleaseStateWith } from "./depositRules";

// Phase 4: ledger_entries is now a Postgres view derived from the GL
// (acct_journal_lines + acct_journal_entries). There is nothing to
// insert into — every row is computed live from the journal lines
// posted to per-tenant AR accounts.
//
// The function is kept as a no-op shim so any leftover legacy caller
// doesn't crash. Returns true to mirror the previous "success" code
// path. New code should use atomicPostJEAndLedger which posts the
// canonical JE; the view will reflect the row automatically.
export async function safeLedgerInsert(_entry) {
  return true;
}

// Atomic JE + ledger + balance in a single DB transaction via RPC
// Falls back to non-atomic postAccountingTransaction if RPC unavailable
export async function atomicPostJEAndLedger({ date, description, reference, property, lines, status, ledgerEntry, balanceUpdate, companyId }) {
  const result = { jeId: null, ledgerOk: false, balanceOk: false, error: null };
  try {
    // Resolve bare GL CODES to real account UUIDs before handing them to
    // the RPC. post_je_and_ledger casts account_id to uuid, but most
    // callers pass a code -- Leases, LateFees, Lifecycle, Owners and
    // Maintenance all post lines like { account_id: "1000" }. Every one
    // of those 400'd with `invalid input syntax for type uuid: "1000"`,
    // the error was swallowed (silent PM-4002), and the helper fell
    // through to the NON-ATOMIC sequential writer. The entry still
    // landed, so nothing looked wrong -- but the atomicity this function
    // exists to provide was never actually in effect for those callers.
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const rpcLines = [];
    for (const l of (lines || [])) {
      let accountId = l.account_id;
      if (accountId && !UUID_RE.test(String(accountId))) {
        accountId = await resolveAccountId(String(accountId), companyId);
        if (!accountId) {
          // Fail loudly here rather than posting a line with no account.
          result.error = `Could not resolve account "${l.account_id}"`;
          return result;
        }
      }
      rpcLines.push({
        account_id: accountId, account_name: l.account_name || "",
        debit: safeNum(l.debit), credit: safeNum(l.credit),
        class_id: l.class_id || null, memo: l.memo || ""
      });
    }
    const { data: jeId, error: rpcErr } = await supabase.rpc("post_je_and_ledger", {
      p_company_id: companyId,
      p_date: date,
      p_description: description,
      p_reference: reference || "",
      p_property: property || "",
      p_status: status || "posted",
      p_lines: rpcLines,
      p_ledger_tenant: ledgerEntry?.tenant || null,
      p_ledger_tenant_id: ledgerEntry?.tenant_id || null,
      p_ledger_property: ledgerEntry?.property || property || null,
      p_ledger_amount: safeNum(ledgerEntry?.amount),
      p_ledger_type: ledgerEntry?.type || null,
      p_ledger_description: ledgerEntry?.description || null,
      p_balance_change: safeNum(balanceUpdate?.amount)
    });
    // The same reference already posted: the unique index refused a second
    // copy (double click, retry, second tab). Report it; never fall back to
    // the sequential writer, which would only fail the same way.
    if (rpcErr?.code === "23505" && /reference/i.test((rpcErr.message || "") + " " + (rpcErr.details || ""))) {
      result.duplicate = true; result.error = "already posted";
      return result;
    }
    if (rpcErr) throw rpcErr;
    result.jeId = jeId;
    result.ledgerOk = !!ledgerEntry;
    result.balanceOk = !!balanceUpdate;
    return result;
  } catch (e) {
    pmError("PM-4002", { raw: e, context: "atomic JE+ledger RPC, falling back to sequential", silent: true });
    return postAccountingTransaction({ date, description, reference, property, lines, status, ledgerEntry, balanceUpdate, requireJE: true, companyId });
  }
}

// Unified accounting transaction: JE → ledger → balance (non-atomic fallback)
export async function postAccountingTransaction({ date, description, reference, property, lines, status, ledgerEntry, balanceUpdate, requireJE = true, silent = false, companyId }) {
  const result = { jeId: null, ledgerOk: false, balanceOk: false, error: null };
  result.jeId = await autoPostJournalEntry({ date, description, reference, property, lines, status, companyId });
  if (!result.jeId && requireJE) {
  result.error = "Journal entry failed";
  return result;
  }
  if (ledgerEntry) {
  const enrichedEntry = { ...ledgerEntry };
  if (!enrichedEntry.tenant_id && balanceUpdate?.tenantId) enrichedEntry.tenant_id = balanceUpdate.tenantId;
  if (balanceUpdate?.tenantId && enrichedEntry.balance === 0) {
  try {
  const { data: tRow } = await supabase.from("tenants").select("balance").eq("id", balanceUpdate.tenantId).eq("company_id", companyId).maybeSingle();
  enrichedEntry.balance = safeNum(tRow?.balance) + safeNum(balanceUpdate.amount);
  } catch (_e) { pmError("PM-6002", { raw: _e, context: "tenant balance lookup for ledger enrichment", silent: true }); }
  }
  result.ledgerOk = await safeLedgerInsert({ company_id: companyId, ...enrichedEntry });
  }
  // When a line lands on this tenant's own AR account the line trigger has
  // already recomputed tenants.balance from the GL; adding the delta as well
  // counted it twice (audit MONEY-8). post_je_and_ledger applies the same
  // rule. The delta is only for a receivable leg on a shared account.
  let balanceAlreadySynced = false;
  if (balanceUpdate?.tenantId && result.jeId) {
    const uuids = (lines || []).map(l => String(l.account_id || "")).filter(id => /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id));
    if (uuids.length) {
      const { data: own } = await supabase.from("acct_accounts").select("id").eq("company_id", companyId).eq("tenant_id", balanceUpdate.tenantId).in("id", uuids).limit(1);
      balanceAlreadySynced = (own || []).length > 0;
    }
  }
  if (balanceAlreadySynced) result.balanceOk = true;
  else if (balanceUpdate?.tenantId) {
  try {
  const { error: balErr } = await supabase.rpc("update_tenant_balance", { p_tenant_id: balanceUpdate.tenantId, p_amount_change: balanceUpdate.amount });
  result.balanceOk = !balErr;
  if (balErr) {
    result.error = balErr.message;
    pmError("PM-6002", { raw: balErr, context: "balance update for tenant " + balanceUpdate.tenantId, silent: true, meta: { tenantId: balanceUpdate.tenantId } });
  }
  } catch (e) { result.error = e.message; pmError("PM-6002", { raw: e, context: "balance RPC exception", silent: true }); }
  }
  return result;
}

// ============ UNIFIED AUTO-POSTING TO ACCOUNTING ============
// Direct insert approach — no RPC. Posts JE header + lines in two steps.
// All bare account codes (e.g., "1000") are resolved to UUIDs via resolveAccountId().
// Read EVERY row of a set-returning RPC.
//
// PostgREST caps a response at 1000 rows, and that cap applies to RPCs just
// as it does to table reads -- silently. A set-returning function read in
// one shot therefore truncates with no error and no indication:
// tests/report-parity.test.js caught acct_account_ledger returning 1000 of
// an account's 2903 lines, with a closing balance of $8,376.58 where the
// truth was -$105,080.99. A ledger that shows two thirds of itself and a
// confident wrong total is worse than one that fails.
//
// Pages until a short page arrives, the same contract fetchAllPaged uses for
// tables. Throws on error rather than returning a partial set, so a caller
// cannot mistake "the fetch broke" for "the account has fewer lines".
export async function rpcAllPaged(fn, args, pageSize = 1000) {
  const rows = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await supabase.rpc(fn, args).range(from, from + pageSize - 1);
    if (error) throw error;
    if (!data || !data.length) break;
    rows.push(...data);
    if (data.length < pageSize) break;
  }
  return rows;
}

// A period is locked THROUGH lock_date, inclusive -- the DB triggers
// (trg_acct_enforce_period_lock*) refuse `date <= lock_date`. This used to
// test `<`, so an entry dated exactly on the lock date passed the client
// check and then failed at the database with a generic error.
// Compares the YYYY-MM-DD part only, so a timestamp can't slip past a
// lexical comparison ("2026-01-31T.." > "2026-01-31").
export async function checkPeriodLock(companyId, date) {
  if (!date || !companyId) return false;
  const { data } = await supabase.from("accounting_period_lock").select("lock_date").eq("company_id", companyId).maybeSingle();
  if (!data?.lock_date) return false;
  return String(date).slice(0, 10) <= String(data.lock_date).slice(0, 10);
}

export async function autoPostJournalEntry({ date, description, reference, property, lines, status = "posted", companyId }) {
  try {
  if (!companyId) { pmError("PM-4002", { raw: { message: "missing companyId" }, context: "autoPostJournalEntry", silent: true }); return null; }
  // DR=CR balance guard. Interactive JE creation validates via validateJE(),
  // but every programmatic caller (autopay, recurring rent, owner dist,
  // late fees) used to bypass that check — letting unbalanced JEs post and
  // corrupt the GL silently. Reject at the gate so callers fail fast and
  // the nightly integrity sweep has less to clean up.
  if (lines?.length > 0) {
    const trDebit = lines.reduce((s, l) => s + safeNum(l.debit), 0);
    const trCredit = lines.reduce((s, l) => s + safeNum(l.credit), 0);
    if (Math.abs(trDebit - trCredit) > 0.005) {
      pmError("PM-4001", {
        raw: { message: `JE out of balance: DR $${trDebit.toFixed(2)} vs CR $${trCredit.toFixed(2)}` },
        context: `autoPostJournalEntry ref=${reference || "(none)"} desc=${description || "(none)"}`,
        meta: { trDebit, trCredit, diff: trDebit - trCredit, lineCount: lines.length },
      });
      return null;
    }
  }
  // Period lock check
  if (await checkPeriodLock(companyId, date)) { pmError("PM-4004", { raw: { message: "blocked by period lock" }, context: "autoPostJournalEntry, date: " + date, silent: true }); return null; }
  const cid = companyId;
  // Resolve bare account codes to UUIDs — work on a COPY to avoid mutating caller's data
  const resolvedLines = lines?.length > 0 ? lines.map(l => ({ ...l })) : [];
  // Any code, not only a bare 4-digit one: a tenant's own receivable is
  // "1100-001", and sending that to the uuid column failed the whole entry
  // (opening balances, audit MONEY-10). An unknown code posts nothing.
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  for (let i = 0; i < resolvedLines.length; i++) {
  const code = resolvedLines[i].account_id;
  if (code && !UUID_RE.test(String(code))) {
    const id = await resolveAccountId(String(code), cid);
    if (!id) { pmError("PM-4002", { raw: { message: `account "${code}" is not in the chart of accounts` }, context: "autoPostJournalEntry" }); return null; }
    resolvedLines[i].account_id = id;
  }
  }
  // Step 1: Insert journal entry header. The number is left blank: the
  // database claims the next one inside this insert, under a lock
  // (trg_je_assign_number, audit MONEY-25), so two people posting at once
  // no longer draw the same number. The one unique index left to trip is
  // idx_je_company_reference_unique: the caller asked to post the same
  // deterministic reference twice, which is a no-op, not an error. We don't
  // pmError() it: every login + wizard + manual run of the recurring engine
  // fires this benignly, and logging it produced 119/week of pure noise.
  const { data: jeRow, error: jeErr } = await supabase.from("acct_journal_entries").insert([{
  company_id: cid, number: null, date, description, reference: reference || "", property: property || "", status
  }]).select("id").maybeSingle();
  const isReferenceCollision = /\b(reference)\b|idx_je_company_reference_unique/i.test((jeErr?.message || "") + " " + (jeErr?.details || ""));
  if (isReferenceCollision) return null;
  if (jeErr || !jeRow) {
    // Defensive: any 23505 (unique-violation) that fell through the
    // classifier above is still a dedup at the SQL level — the caller's
    // intent was idempotent, so suppress the user-facing toast. Without
    // this catch-all, an unindexed unique constraint or a renamed index
    // (we've shipped two over the lifetime of acct_journal_entries) leaks
    // a PM-9005 toast on every dashboard load that auto-posts an
    // already-posted JE.
    if (jeErr?.code === "23505") {
      // 23505 unique-violation fallthrough: same idempotent dedup story
      // as the explicit isReferenceCollision branch above. Suppress.
      return null;
    }
    pmError("PM-4002", { raw: jeErr, context: "journal entry insert" });
    return null;
  }
  // Step 2: Insert journal entry lines (with company_id for RLS)
  if (resolvedLines.length > 0) {
  const { error: lineErr } = await supabase.from("acct_journal_lines").insert(resolvedLines.map(l => ({
  journal_entry_id: jeRow.id, company_id: cid,
  account_id: l.account_id, account_name: l.account_name || "",
  debit: safeNum(l.debit), credit: safeNum(l.credit),
  class_id: l.class_id || null, memo: l.memo || ""
  })));
  if (lineErr) {
  pmError("PM-4003", { raw: lineErr, context: "journal lines insert" });
  // Clean up orphan header — if cleanup fails, void it instead so it's visible but harmless
  { const { error: _delErr } = await supabase.from("acct_journal_entries").delete().eq("id", jeRow.id).eq("company_id", companyId);
    if (_delErr) {
      // Delete failed (RLS, network) — void the orphan so it doesn't affect reports
      await supabase.from("acct_journal_entries").update({ status: "voided", description: "[ORPHANED — lines failed] " + (description || "") }).eq("id", jeRow.id).eq("company_id", companyId);
      pmError("PM-4012", { raw: _delErr, context: "ORPHANED JE HEADER: delete failed, voided instead. JE ID: " + jeRow.id, silent: false });
    }
  }
  return null;
  }
  }
  return jeRow.id;
  } catch (e) { pmError("PM-4002", { raw: e, context: "auto-post journal entry" }); return null; }
}

// Does the tenant have a RENT CHARGE (accrual) dated in `month` ("YYYY-MM")?
//
// This used to look only for references "RENT-AUTO-%" / "ACCR-%", which
// nothing produces any more -- real rent is RECUR-<schedule id8>-YYYY-MM
// (autoPostRecurringEntries) and RENT1-/PRORENT- (the property wizard). It
// therefore answered "no" for every tenant, which is why autoOwnerDistribution
// always bailed. Now: with a tenantId, the question is answered from the
// tenant's OWN AR account(s) -- any debit of a rent-charge family
// (paymentRules.RENT_CHARGE_PREFIXES) dated in the month. Without a tenantId
// the legacy name-in-memo match is kept, widened to every rent family.
//
// A failed read answers TRUE ("could not tell"), as before: the caller must
// not mistake a broken query for "no rent was billed".
export async function checkAccrualExists(companyId, month, tenantName, tenantId) {
  if (tenantId !== null && tenantId !== undefined && tenantId !== "") {
    // Shared with the Stripe webhook (utils/ownerRules.js).
    return tenantRentChargeInMonth(supabase, companyId, month, tenantId);
  }
  // Legacy: no tenant id. Match rent-family entries in the month whose lines
  // mention the tenant by name. Paged, chunked at 100 for .in().
  const { start, end } = monthBounds(month);
  const orFilter = RENT_CHARGE_PREFIXES.map(p => `reference.like.${escapeFilterValue(p)}%`).join(",");
  const { rows: rentJEs, failed: rentFailed } = await fetchAllPaged(
    () => supabase.from("acct_journal_entries")
      .select("id, reference").eq("company_id", companyId)
      .or(orFilter).gte("date", start).lte("date", end)
      .neq("status", "voided").order("id"),
    "rent accrual entries",
  );
  if (rentFailed) return true;
  if (!rentJEs || rentJEs.length === 0) return false;
  const jeIds = rentJEs.map(je => je.id);
  const needle = String(tenantName || "").toLowerCase();
  if (!needle) return false;
  for (let i = 0; i < jeIds.length; i += 100) {
    const { data: lines } = await supabase.from("acct_journal_lines")
      .select("journal_entry_id, memo").in("journal_entry_id", jeIds.slice(i, i + 100));
    if ((lines || []).some(l => l.memo && l.memo.toLowerCase().includes(needle))) return true;
  }
  return false;
}

// ============ OWNER DISTRIBUTION AUTOMATION ============
// After a rent receipt on an owner-managed property, bring the tenant's owner
// accruals up to date (DR rent income / CR 4200 fee / CR 2200 net, one
// owner_distributions 'accrual' row per receipt-to-charge allocation).
//
// The work is the SQL function owner_accrual_sync (migration 20260928170000):
// rent-first, oldest-first allocation of every receipt to the tenant's rent
// charges in any month, in one transaction under a per-tenant lock, keyed by
// tenant_id + receipt entry + charge entry -- never by name or calendar
// month. Triggers already run it on every receipt, charge and void; this call
// (autopay Run Now, manual ledger payments, Banking deposits; the Stripe
// webhook calls the same RPC) returns the result and is idempotent. The
// property, amount, date and name arguments are kept for the callers'
// signature; only the tenant id matters.
export async function autoOwnerDistribution(companyId, propertyAddress, paymentAmount, paymentDate, tenantName, tenantId) {
  const res = await syncOwnerAccruals(supabase, companyId, tenantId);
  if (res.error) pmError("PM-6004", { raw: { message: res.error }, context: "owner accrual sync", silent: true });
  return res;
}

// Resolve property address to cost-center class ID (with caching)
// Strategy: properties.class_id is the source of truth. If null, create the class and store it.
export const _classIdCache = {};
export async function getPropertyClassId(propertyAddress, companyId) {
  if (!propertyAddress || !companyId) return null;
  const cacheKey = `${companyId}::${propertyAddress}`;
  if (_classIdCache[cacheKey] !== undefined) return _classIdCache[cacheKey];
  // 1. Look up property's stored class_id (authoritative)
  const { data: prop } = await supabase.from("properties").select("id, class_id").eq("company_id", companyId).eq("address", propertyAddress).maybeSingle();
  if (prop?.class_id) {
  // Verify the class still exists
  const { data: cls } = await supabase.from("acct_classes").select("id").eq("id", prop.class_id).eq("company_id", companyId).maybeSingle();
  if (cls?.id) { _classIdCache[cacheKey] = cls.id; return cls.id; }
  }
  // 2. class_id is null or stale — find or create the class by exact name match
  const { data: exactMatch } = await supabase.from("acct_classes").select("id").eq("name", propertyAddress).eq("company_id", companyId).maybeSingle();
  if (exactMatch?.id) {
  // Update property to store this class_id for future lookups
  if (prop?.id) await supabase.from("properties").update({ class_id: exactMatch.id }).eq("id", prop.id).eq("company_id", companyId);
  _classIdCache[cacheKey] = exactMatch.id;
  return exactMatch.id;
  }
  // 3. No class exists — create one and store on property
  const { data: newClass } = await supabase.from("acct_classes").insert([{
  id: crypto.randomUUID(), name: propertyAddress, description: "Auto-created for " + propertyLabel(propertyAddress),
  color: pickColor(propertyAddress), is_active: true, company_id: companyId,
  }]).select("id").maybeSingle();
  if (newClass?.id) {
  if (prop?.id) await supabase.from("properties").update({ class_id: newClass.id }).eq("id", prop.id).eq("company_id", companyId);
  _classIdCache[cacheKey] = newClass.id;
  return newClass.id;
  }
  _classIdCache[cacheKey] = null;
  return null;
}

// ============ ACCOUNT CODE RESOLUTION ============
// Maps bare account codes ("1000") to UUID primary keys in acct_accounts.
// Uses the `code` column. Falls back to name matching. Auto-creates missing accounts.
export const _acctIdCache = {};
export const _acctCodeToName = { "1000": "Checking Account", "1100": "Accounts Receivable", "2100": "Security Deposits Held", "2110": "Accounts Payable", "2200": "Owner Distributions Payable", "3000": "Opening Balance Equity", "3100": "Owner's Equity", "3200": "Retained Earnings", "4000": "Rental Income", "4010": "Late Fee Income", "4100": "Other Income", "4200": "Management Fee Income", "5300": "Repairs & Maintenance", "5400": "Utilities Expense", "5450": "HOA Fees", "5500": "Bad Debt Expense", "5600": "Mortgage/Loan Payment", "5610": "Legal & Eviction Costs", "5710": "Property Taxes" };
export async function resolveAccountId(bareCode, companyId) {
  if (!companyId) return null;
  const cid = companyId;
  if (!_acctIdCache[cid]) _acctIdCache[cid] = {};
  if (_acctIdCache[cid][bareCode]) return _acctIdCache[cid][bareCode];
  // Bulk-fetch all accounts and cache by code, name, and old suffix patterns
  const { data: allAccts } = await supabase.from("acct_accounts").select("id, code, name").eq("company_id", cid);
  if (allAccts && allAccts.length > 0) {
  for (const a of allAccts) {
  // Cache by code column (primary lookup)
  if (a.code) _acctIdCache[cid][a.code] = a.id;
  // Cache by name → standard code (fallback for migrated accounts)
  for (const [code, name] of Object.entries(_acctCodeToName)) {
  if (a.name === name && !_acctIdCache[cid][code]) _acctIdCache[cid][code] = a.id;
  }
  // Cache by old compound suffix (e.g., "co-abc-1000" → cache under "1000")
  if (a.code) {
  const suffix = a.code.match(/(\d{4,})$/);
  if (suffix && !_acctIdCache[cid][suffix[1]]) _acctIdCache[cid][suffix[1]] = a.id;
  }
  }
  }
  if (_acctIdCache[cid][bareCode]) return _acctIdCache[cid][bareCode];
  // Auto-create missing account with UUID PK + bare code
  const acctName = _acctCodeToName[bareCode] || "Account " + bareCode;
  const acctType = bareCode[0] === "1" ? "Asset" : bareCode[0] === "2" ? "Liability" : bareCode[0] === "3" ? "Equity" : bareCode[0] === "4" ? "Revenue" : "Expense";
  const { data: created, error: createErr } = await supabase.from("acct_accounts").insert([{
  company_id: cid, code: bareCode, name: acctName, type: acctType, is_active: true, old_text_id: cid + "-" + bareCode
  }]).select("id").maybeSingle();
  if (createErr) pmError("PM-4006", { raw: createErr, context: "resolveAccountId auto-create for " + bareCode, silent: true });
  const resolvedId = created?.id || null;
  if (resolvedId) _acctIdCache[cid][bareCode] = resolvedId;
  return resolvedId;
}

// ============ TENANT AR SUB-ACCOUNT ============
// Creates or retrieves a per-tenant AR sub-account (e.g., "1100-001 AR - Alice Johnson")
// linked to the parent 1100 Accounts Receivable account.
export const _tenantArCache = {};
// Resolve (or create) the AR sub-account for ONE tenant row. The
// cache key uses tenantId so a tenant who appears at multiple
// properties (e.g. Sahil Agarwal × 3 leases) gets one AR per lease,
// not one shared AR keyed by name. The shared-by-name behavior
// blended unrelated leases into a single $42K consolidated balance
// at Smith and made per-lease reporting impossible — see migration
// 20260424000010 which fixed acct_accounts.tenant_id to bigint and
// the split script that re-attributed legacy lines.
//
// Lookup precedence:
//   1. tenantId-keyed acct_accounts row (the new normal)
//   2. Legacy "AR - <name>" account ONLY if the tenantId case is
//      unique for this name (i.e. no other tenants row at this
//      company shares the name) — preserves the historical
//      sub-account for single-lease tenants without creating a
//      duplicate.
//   3. Create a new "AR - <name> (<short property>)" sub-account
//      with tenant_id populated.
// ---------------------------------------------------------------------------
// SECURITY DEPOSIT IDEMPOTENCY
//
// Four separate places post "Security deposit received": the property setup
// wizard, the property form, the Tenants page and the Leases page. Three of
// them used `"DEP-" + shortId()` -- a RANDOM reference -- so no path could see
// another path's posting and none could see its own on a re-save. Onboard a
// tenant on the Tenants page and then save the wizard, and the deposit was on
// the books twice. The sandbox company has one property carrying five of them,
// same date, five different random references.
//
// The fix is a single deterministic reference for all four. That turns
// idempotency from something each caller has to remember into something the
// DATABASE enforces: idx_je_company_reference_unique is UNIQUE on
// (company_id, reference) WHERE status <> 'voided' AND reference <> '', so a
// second insert is refused no matter which code path attempts it -- including
// paths written later that never read this comment.
//
// Keying on the tenant and nothing else is deliberate. An earlier scheme put
// the lease start date in the reference, which broke the moment a PM corrected
// that date: the key changed, the duplicate check missed, and the deposit
// posted again. Only one security deposit per tenant is ever auto-posted; an
// additional or increased deposit is an adjustment and belongs in a manual
// journal entry with its own reference.
//
// Because the unique index excludes voided entries, voiding a deposit JE
// correctly frees the reference and allows a genuine re-post.
//
// Returns null when there is no tenant id. That matters: 'DEP-T' + undefined
// is 'DEP-Tundefined', a single string the unique index would then allow only
// ONE of per company -- silently blocking every later tenant-less deposit.
// Callers fall back to a random reference in that case, which is exactly the
// old behaviour: no cross-path dedup is possible without an id to key on.
export const depositReference = (tenantId) =>
  (tenantId === null || tenantId === undefined || tenantId === '') ? null : 'DEP-T' + tenantId;

// True if this tenant already has a security deposit on the books.
//
// Checks the canonical reference and also the wizard's older
// DEP-T<id>-<leaseStart> form, which real data still carries. Deposits written
// by the three random-reference paths BEFORE this change cannot be recognised
// from the reference at all -- nothing in the string ties them to a tenant --
// so they are invisible here and the unique index is what stops the next one.
//
// Fails CLOSED: a lookup that errors reports "already posted" rather than
// letting a duplicate through on the back of a network blip.
export async function depositAlreadyPosted(companyId, tenantId) {
  if (!companyId || !tenantId) return false;
  try {
    const canonical = depositReference(tenantId);
    const [exact, legacy] = await Promise.all([
      supabase.from('acct_journal_entries').select('id')
        .eq('company_id', companyId).eq('reference', canonical)
        .neq('status', 'voided').limit(1),
      // The trailing hyphen keeps T12 from matching T123.
      supabase.from('acct_journal_entries').select('id')
        .eq('company_id', companyId).neq('status', 'voided')
        .like('reference', escapeFilterValue(canonical + '-') + '%').limit(1),
    ]);
    if (exact.error || legacy.error) return true;
    if ((exact.data || []).length > 0 || (legacy.data || []).length > 0) return true;

    // Cross-source catch. QB-imported and older deposits carry no DEP-T
    // reference, so the checks above cannot see them — which is exactly how a
    // re-onboarded tenant got a SECOND deposit posted on top of the one from
    // QuickBooks. Fall back to the accounting shape: any non-voided debit on
    // this tenant's own AR sub-account whose memo names a deposit. Keyed on
    // tenant_id, so it holds regardless of how the first deposit was written.
    const { data: arAcct } = await supabase.from('acct_accounts')
      .select('id').eq('company_id', companyId).eq('tenant_id', tenantId)
      .like('code', '1100-%').eq('is_active', true).limit(1).maybeSingle();
    if (arAcct?.id) {
      const { data: depLines, error: depErr } = await supabase.from('acct_journal_lines')
        .select('id').eq('company_id', companyId).eq('account_id', arAcct.id)
        .gt('debit', 0).ilike('memo', '%deposit%').limit(1);
      if (depErr) return true;
      if ((depLines || []).length > 0) return true;
    }
    return false;
  } catch (_e) {
    return true;
  }
}

// ---------------------------------------------------------------------------
// OWED, THEN PAID
//
// A security deposit is booked as a charge on the tenant -- DR the tenant's
// OWN AR sub-account / CR 2100 Security Deposits Held -- and the tenant then
// pays it like rent (the payment credits the same AR). Every path that books
// a deposit (wizard, property form, Tenants page, Leases page) uses this to
// find that account. getOrCreateTenantAR falls back to the shared 1100 parent
// (or a name-only legacy account) when it cannot find or create one keyed on
// the tenant; neither is this tenant's receivable, so null is returned and
// the caller refuses to post rather than parking the charge on a shared
// account where no payment will ever meet it.
export async function tenantOwnArAccountId(companyId, tenantName, tenantId) {
  if (!companyId || tenantId === null || tenantId === undefined || tenantId === "") return null;
  const id = await getOrCreateTenantAR(companyId, tenantName, tenantId);
  if (!id) return null;
  const { data, error } = await supabase.from("acct_accounts").select("id, tenant_id")
    .eq("company_id", companyId).eq("id", id).maybeSingle();
  if (error || !data || data.tenant_id === null || data.tenant_id === undefined) return null;
  return String(data.tenant_id) === String(tenantId) ? data.id : null;
}

// ---------------------------------------------------------------------------
// SECURITY DEPOSIT RELEASE -- never twice. Rules and the reference scheme
// (DEPRET-T<tenant id>, or DEPRET-L<lease id> for a lease with no tenant id)
// live in depositRules.js; this binds the check to the app's client.
export { RELEASED_DEPOSIT_STATUSES, depositReleaseKey, depositReleaseReference, depositDeductionReference, depositReleaseReferences, decideDepositRelease, depositReturnOfferable, planReleaseLegs, releasedDepositStatus, isKeyedReleaseRef };
export function depositReleaseState(companyId, { tenantId = null, leaseId = null } = {}) {
  return depositReleaseStateWith(supabase, companyId, { tenantId, leaseId });
}

// What the tenant owes right now, from the GL: debits minus credits on their
// own AR account(s) (acct_accounts.tenant_id), posted entries only -- the
// same definition as recompute_tenant_balance.
// null when it cannot be read -- callers then show no amount rather than 0.
export async function tenantOwedFromGL(companyId, tenantId) {
  if (!companyId || tenantId === null || tenantId === undefined || tenantId === "") return null;
  const { data: accts, error } = await supabase.from("acct_accounts").select("id")
    .eq("company_id", companyId).eq("type", "Asset").eq("tenant_id", tenantId);
  if (error) return null;
  const ids = (accts || []).map(a => a.id);
  if (!ids.length) return 0;
  const { rows, failed } = await fetchAllPaged(() => supabase.from("acct_journal_lines")
    .select("debit, credit, acct_journal_entries!inner(status)")
    .eq("company_id", companyId).in("account_id", ids)
    .eq("acct_journal_entries.status", "posted").order("id"), "tenant owed");
  if (failed) return null;
  return Math.round(rows.reduce((s, l) => s + safeNum(l.debit) - safeNum(l.credit), 0) * 100) / 100;
}

// The tenant's OWN AR sub-account (1100-NNN, tenant_id set), found or made.
// Same rules as the SQL helper _late_fee_tenant_ar (which the Stripe webhook
// uses under a lock):
//   1. by tenant_id -- the ACTIVE linked account, else the lowest code. A
//      tenant with two linked accounts used to make .maybeSingle() error,
//      which fell through to step 3 and left a junk account behind.
//   2. legacy "AR - <name>" -- only when that name is unambiguous AND the
//      account is not linked to another tenant (pickLegacyNamedArAccount).
//      A returning tenant whose old account belongs to their archived
//      tenant row gets a new account, never the old row's.
//   3. create 1100-NNN linked to the tenant under the 1100 parent. On a
//      unique/code collision (a concurrent creator), re-read step 1 and
//      otherwise try the next code. It never retries without tenant_id /
//      parent -- that retry was what littered the chart with unlinked
//      "AR - <name>" accounts.
// Falls back to the 1100 parent only when all of that fails (not cached, so
// the next call tries again); callers that must post to the tenant's own
// account check tenant_id on the result.
export async function getOrCreateTenantAR(companyId, tenantName, tenantId) {
  try {
  if (!companyId || !tenantName) return await resolveAccountId("1100", companyId);
  const cacheKey = `${companyId}::${tenantId || tenantName}`;
  if (_tenantArCache[cacheKey]) return _tenantArCache[cacheKey];
  const hasTid = tenantId !== null && tenantId !== undefined && tenantId !== "";
  // 1. By tenant_id (the per-lease key). Several rows are possible.
  const findLinked = async () => {
    const { data: linked, error } = await supabase.from("acct_accounts")
      .select("id, code, name, is_active, tenant_id").eq("company_id", companyId).eq("type", "Asset")
      .eq("tenant_id", tenantId).limit(50);
    if (error) return { error };
    return { pick: pickTenantArAccount(linked || [], tenantId) };
  };
  if (hasTid) {
    const { pick, error } = await findLinked();
    if (error) throw error;
    if (pick?.id) { _tenantArCache[cacheKey] = pick.id; return pick.id; }
  }
  // 2. Legacy fallback by exact name.
  const { data: named } = await supabase.from("acct_accounts")
    .select("id, tenant_id").eq("company_id", companyId).eq("type", "Asset")
    .eq("name", "AR - " + tenantName).limit(10);
  const existing = pickLegacyNamedArAccount(named || [], hasTid ? tenantId : null);
  if (existing?.id) {
    if (hasTid) {
      // Same-name multiplicity check: two ACTIVE tenants sharing the name
      // must each get their own account, not share the consolidated one.
      const { data: sib } = await supabase.from("tenants").select("id")
        .eq("company_id", companyId).eq("name", tenantName).is("archived_at", null);
      if ((sib || []).length <= 1) {
        if (existing.tenant_id) { _tenantArCache[cacheKey] = existing.id; return existing.id; }
        // Adopt it -- only while it is still unlinked, so two callers cannot
        // both claim it.
        const { data: adopted } = await supabase.from("acct_accounts").update({ tenant_id: tenantId })
          .eq("id", existing.id).eq("company_id", companyId).is("tenant_id", null).select("id");
        if ((adopted || []).length > 0) { _tenantArCache[cacheKey] = existing.id; return existing.id; }
        // Linked by someone else meanwhile (or refused): whatever this
        // tenant now has wins; otherwise create below.
        const again = await findLinked();
        if (again.pick?.id) { _tenantArCache[cacheKey] = again.pick.id; return again.pick.id; }
      }
    } else {
      _tenantArCache[cacheKey] = existing.id;
      return existing.id;
    }
  }
  // 3. Create a new per-lease sub-account.
  const parentArId = await resolveAccountId("1100", companyId);
  // Look up the tenant's property to disambiguate the account name.
  let acctName = "AR - " + tenantName;
  if (hasTid) {
    const { data: tRow } = await supabase.from("tenants").select("property").eq("company_id", companyId).eq("id", tenantId).maybeSingle();
    // Deliberately NOT propertyLabel(): this is a MATCHING key compared
    // against values built the same way elsewhere, not a label. Making it
    // include the unit would stop it matching.
    const shortProp = (tRow?.property || "").split(",")[0].trim();
    if (shortProp) acctName = "AR - " + tenantName + " (" + shortProp + ")";
  }
  let newAcct = null, createErr = null;
  for (let attempt = 0; attempt < 5; attempt++) {
    const { data: subAccts } = await supabase.from("acct_accounts").select("code").eq("company_id", companyId).like("code", "1100-%").order("code", { ascending: false }).limit(1000);
    const newCode = "1100-" + String(nextTenantArSeq((subAccts || []).map(r => r.code), attempt)).padStart(3, "0");
    const oldTextId = companyId + "-" + newCode;
    ({ data: newAcct, error: createErr } = await supabase.from("acct_accounts").insert([{
      company_id: companyId, code: newCode, name: acctName,
      type: "Asset", is_active: true, old_text_id: oldTextId,
      parent_id: parentArId || null, tenant_id: hasTid ? tenantId : null,
    }]).select("id").maybeSingle());
    if (!createErr && newAcct?.id) break;
    newAcct = null;
    const unique = createErr?.code === "23505" || /duplicate|unique|already has an active AR/i.test(createErr?.message || "");
    if (!unique) break;
    // A concurrent creator may have made this tenant's account: use it.
    if (hasTid) {
      const again = await findLinked();
      if (again.pick?.id) { _tenantArCache[cacheKey] = again.pick.id; return again.pick.id; }
    }
  }
  if (createErr || !newAcct?.id) {
    pmError("PM-4006", { raw: createErr, context: "AR sub-account creation", silent: true });
    return parentArId;
  }
  _tenantArCache[cacheKey] = newAcct.id;
  return newAcct.id;
  } catch (e) {
  pmError("PM-4006", { raw: e, context: "get or create tenant AR sub-account", silent: true });
  return await resolveAccountId("1100", companyId);
  }
}

// INTENTIONAL NO-OP: Rent is handled exclusively by autoPostRecurringEntries via recurring_journal_entries table.
// This stub exists for backward compatibility — called in handleSelectCompany and wizard completion.
export async function autoPostRentCharges() { return { posted: 0, failed: 0 }; }

// ============ RECURRING RENT: who may be billed, and where ============
//
// A tenant's rent schedule (recurring_journal_entries with tenant_id set)
// outlived the tenancy it billed. Archiving a tenant, terminating a lease,
// completing an eviction, deactivating a property and bulk-marking tenants
// "past" all left the schedule active, so rent kept accruing on people who
// had left. And a schedule's debit account was captured once at creation,
// so a tenant whose AR sub-account was later split or re-created kept
// being billed to the old one -- or to a sibling's -- while two schedules
// for the same tenant could bill the same AR account twice in one month.
//
// The pure rules live in recurringRules.js (no imports, so tests can load
// them in plain node) and are re-exported here for callers.
export { BILLABLE_LEASE_STATUSES, isTenantBillable, recurringTenantSkipReason, pickTenantArAccount, monthBounds, arAlreadyBilledInMonth };

// Stop a tenant's rent. Same shape move_out_commit_state uses: active
// schedules become status 'inactive' and are archived. Keyed on tenant_id.
export async function deactivateTenantRecurring(companyId, tenantIds) {
  const ids = [...new Set([].concat(tenantIds ?? []).filter(v => v !== null && v !== undefined && v !== "").map(Number).filter(Number.isFinite))];
  if (!companyId || ids.length === 0) return { ok: true, count: 0 };
  let count = 0;
  for (let i = 0; i < ids.length; i += 100) {
    const chunk = ids.slice(i, i + 100);
    const { data, error } = await supabase.from("recurring_journal_entries")
      .update({ status: "inactive", archived_at: new Date().toISOString() })
      .eq("company_id", companyId).in("tenant_id", chunk)
      .eq("status", "active").is("archived_at", null).select("id");
    if (error) {
      pmError("PM-4008", { raw: error, context: "deactivate recurring rent for tenant(s) " + chunk.join(","), silent: true });
      return { ok: false, count, error };
    }
    count += (data || []).length;
  }
  return { ok: true, count };
}

// Point a tenant's live rent schedule(s) at a new amount. One function for
// the tenant edit, lease renewal and rent increase. Live = not archived and
// active or paused (a paused schedule must resume at the new rent).
export async function syncTenantRecurringAmount(companyId, tenantId, amount) {
  if (!companyId || tenantId === null || tenantId === undefined || tenantId === "") return { ok: true, count: 0, previousAmount: null };
  const live = (q) => q.eq("company_id", companyId).eq("tenant_id", Number(tenantId))
    .in("status", ["active", "paused"]).is("archived_at", null);
  // The RENT schedule only (credits Rental Income 4000): pet rent, parking
  // or a reimbursement schedule keeps its own amount (audit DOC-23 / 5.3).
  const { data: rentAcct } = await supabase.from("acct_accounts").select("id").eq("company_id", companyId).eq("code", "4000").limit(1).maybeSingle();
  const rentIds = ["4000", ...(rentAcct?.id ? [String(rentAcct.id)] : [])];
  const isRent = (r) => rentIds.includes(String(r.credit_account_id || "")) || /^rental income/i.test(String(r.credit_account_name || ""));
  const { data: all, error: selErr } = await live(supabase.from("recurring_journal_entries").select("id, amount, credit_account_id, credit_account_name"));
  const rows = (all || []).filter(isRent);
  if (selErr) {
    pmError("PM-4008", { raw: selErr, context: "check recurring amount after rent change", silent: true });
    return { ok: false, count: 0, previousAmount: null, error: selErr, stage: "check" };
  }
  if (!rows || rows.length === 0) return { ok: true, count: 0, previousAmount: null };
  const { error: upErr } = await live(supabase.from("recurring_journal_entries").update({ amount: safeNum(amount) })).in("id", rows.map(r => r.id));
  if (upErr) {
    pmError("PM-4008", { raw: upErr, context: "update recurring amount after rent change", silent: true });
    return { ok: false, count: 0, previousAmount: rows[0].amount, error: upErr, stage: "update" };
  }
  return { ok: true, count: rows.length, previousAmount: rows[0].amount };
}

// ============ AUTO-POST RECURRING JOURNAL ENTRIES ============
// Posts every recurring period that is due and not yet posted. The engine is
// the database function post_due_recurring (migration 20261004210000): the
// server runs it every morning (api/daily-reminders?task=rent), and the staff
// buttons ("Post what is due", after creating a schedule or a tenancy) run
// the same function -- one set of rules, one runner per company at a time.
// It used to be this browser code, run on every login (audit MONEY-19,
// OPS-16).
export async function autoPostRecurringEntries(companyId) {
  if (!companyId) return { posted: 0 };
  const { data, error } = await supabase.rpc("post_due_recurring", { p_company_id: companyId });
  if (error) { pmError("PM-4008", { raw: error, context: "post due recurring entries", silent: true }); return { posted: 0, error }; }
  const posted = Number(data?.posted) || 0;
  if (posted > 0) logAudit("create", "accounting", "Posted " + posted + " recurring entries", "", "system", "system", companyId);
  return { posted, skipped: data?.skipped || [] };
}

// ZIP → City/State lookup (Zippopotam.us — free, no API key).
// Wraps fetch with a 5s AbortController so a Zippopotam outage can't
// hang the property-setup wizard indefinitely (the lookup blocks the
// form advance until it resolves).
export const _zipCache = {};
export async function lookupZip(zip) {
  if (!/^\d{5}$/.test(zip)) return null;
  if (_zipCache[zip]) return _zipCache[zip];
  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), 5000) : null;
  try {
  const r = await fetch("https://api.zippopotam.us/us/" + zip, ctrl ? { signal: ctrl.signal } : undefined);
  if (!r.ok) return null;
  const data = await r.json();
  const place = data.places?.[0];
  if (!place) return null;
  const result = { city: place["place name"], state: place["state abbreviation"] };
  _zipCache[zip] = result;
  return result;
  } catch (_e) { pmError("PM-8006", { raw: _e, context: "ZIP code lookup", silent: true }); return null; }
  finally { if (timer) clearTimeout(timer); }
}


// ============ OPENING BALANCE JE ============
// Posts the one-and-only opening balance journal entry for a company.
// Dedup key: reference = "OPENING-BALANCE-<companyId>", which the
// idx_je_company_reference_unique index enforces at the DB level.
// Calling this twice is a safe no-op.
//
// Input shape:
//   balances: [{ code, name, type, amount }]
//     - type: "Asset" | "Liability" | "Equity" | "Revenue" | "Expense".
//     - amount: signed. Positive = the type's native side (DR for
//       Asset/Expense, CR for Liability/Equity/Revenue). Negative
//       means contra-balance (e.g. accumulated depreciation) and
//       lands on the opposite side.
//
// The plug to 3000 Opening Balance Equity squares the DR/CR totals.
// After post, the user reclassifies OBE → Retained Earnings /
// Owner's Equity via a normal JE; once that's done OBE should be $0.
export async function postOpeningBalanceJE({ companyId, date, balances, userEmail }) {
  if (!companyId) throw new Error("postOpeningBalanceJE: companyId required");
  if (!date) throw new Error("postOpeningBalanceJE: date required");
  const DR_NORMAL = new Set(["Asset", "Expense"]);
  const lines = [];
  let totalDR = 0, totalCR = 0;
  for (const b of (balances || [])) {
    const amt = safeNum(b.amount);
    if (Math.abs(amt) < 0.005) continue;
    const nativeDR = DR_NORMAL.has(b.type);
    const onDebit = (nativeDR && amt >= 0) || (!nativeDR && amt < 0);
    const abs = Math.abs(amt);
    lines.push({
      account_id: b.code,
      account_name: b.name || b.code,
      debit: onDebit ? abs : 0,
      credit: onDebit ? 0 : abs,
      memo: "Opening balance as of " + date,
    });
    if (onDebit) totalDR += abs; else totalCR += abs;
  }

  const diff = totalDR - totalCR;
  if (Math.abs(diff) >= 0.005) {
    if (diff > 0) {
      lines.push({ account_id: "3000", account_name: "Opening Balance Equity", debit: 0, credit: diff, memo: "Opening balance plug" });
    } else {
      lines.push({ account_id: "3000", account_name: "Opening Balance Equity", debit: -diff, credit: 0, memo: "Opening balance plug" });
    }
  }

  if (lines.length === 0) return null;

  return await autoPostJournalEntry({
    companyId,
    date,
    description: "Opening balance as of " + date,
    reference: "OPENING-BALANCE-" + companyId,
    status: "posted",
    lines,
  });
}

// Seed a company's default chart of accounts. Lives here rather than in
// App.js, which is meant to stay a thin router -- the line-budget test
// says to extract rather than raise the cap again.
export async function ensureDefaultAccounts(cid) {
const defaults = [
{ code: "1000", name: "Checking Account", type: "Asset", is_active: true },
{ code: "1100", name: "Accounts Receivable", type: "Asset", is_active: true },
{ code: "2100", name: "Security Deposits Held", type: "Liability", is_active: true },
{ code: "2200", name: "Owner Distributions Payable", type: "Liability", is_active: true },
// Equity block — needed so the Opening Balances tab has a place to
// post prior-system balances. 3000 is the clearing account used as
// the plug on the opening JE; never rename or deactivate without
// reviewing every opening-balance JE that references it.
{ code: "3000", name: "Opening Balance Equity", type: "Equity", is_active: true },
{ code: "3100", name: "Owner's Equity", type: "Equity", is_active: true },
{ code: "3200", name: "Retained Earnings", type: "Equity", is_active: true },
{ code: "4000", name: "Rental Income", type: "Revenue", is_active: true },
{ code: "4010", name: "Late Fee Income", type: "Revenue", is_active: true },
{ code: "4100", name: "Other Income", type: "Revenue", is_active: true },
{ code: "4200", name: "Management Fee Income", type: "Revenue", is_active: true },
{ code: "5300", name: "Repairs & Maintenance", type: "Expense", is_active: true },
{ code: "5400", name: "Utilities Expense", type: "Expense", is_active: true },
];
const { data: existing, error: readErr } = await supabase.from("acct_accounts").select("id, code, name").eq("company_id", cid);
// A failed read looked like "this company has no accounts", so every
// default was treated as missing and re-inserted. Bail instead: seeding
// against an unknown starting state is how duplicates get made.
if (readErr) { pmError("PM-4007", { raw: readErr, context: "ensureDefaultAccounts read existing", silent: true, phase: "read" }); return; }
// Skip on EITHER code or name. Matching name alone missed accounts
// whose code existed under a different name (a renamed "Utilities
// Expense"), and the insert then violated the real constraint,
// (company_id, code): 19 swallowed HTTP 409s per login, which is also
// cover for a real error.
const existingNames = new Set((existing || []).map(a => a.name));
const existingCodes = new Set((existing || []).map(a => String(a.code)));
const missing = defaults.filter(a => !existingNames.has(a.name) && !existingCodes.has(String(a.code)));
if (missing.length === 0) return;
const rows = missing.map(a => ({ ...a, company_id: cid, old_text_id: cid + "-" + a.code }));
for (const row of rows) {
const { error } = await supabase.from("acct_accounts").insert([row]);
// 23505 = unique_violation: another tab or a concurrent login seeded
// it first. That is the desired end state, not a failure worth
// logging -- logging it is what produced the noise.
if (error && error.code !== "23505") pmError("PM-4006", { raw: error, context: "ensureDefaultAccounts insert for " + row.code, silent: true });
}
delete _acctIdCache[cid];
}

// ---------------------------------------------------------------------------
// Read every page of a query, not just the first.
//
// PostgREST caps an unpaged select at 1000 rows and says nothing about it, so
// a caller that forgets this gets a plausible-looking short answer. That is
// the worst shape of bug in an accounting app: a reconciliation missing rows
// past the first thousand still balances to something.
//
// Lived in Accounting.js, in ONE of its components. The reconciler sits in a
// different component in the same file and could not see it -- which is how
// the bank reconciliation came to hold its own ceiling-bound query instead.
export async function fetchAllPaged(build, label) {
// Fetch pages CONCURRENTLY without needing a row count.
//
// This used to walk pages one at a time, waiting for each before
// asking for the next: 17 sequential round trips for the lines alone,
// so wall time was 17x the latency rather than 1x. That is why every
// accounting tab took ~20 seconds on a slow connection.
//
// Deliberately NOT count-based. Chaining a second .select() onto the
// caller's builder to get a count returns count=null with no error —
// which silently collapsed the fetch to a single page. Instead: read
// page 0, and while the last page came back full, request the next
// batch of pages in parallel. Correct by construction, because the
// loop only stops on a short page.
// At most 3 pages at once: 6 parallel reads per call (and several calls
// per page) starved the small database (audit OPS-15).
const PAGE = 1000, MAX_PARALLEL = 3;
// Journal lines are the books (PM-4013, critical); any other list is just
// incomplete (PM-4014). The one code used to blame the journal lines for a
// failed loans or insurance read.
const code = /journal|ledger|lines/i.test(String(label || "")) ? "PM-4013" : "PM-4014";
// A read cut short by the browser itself -- the auth lock "stolen" when
// several requests refresh the token at once, or a dropped connection --
// is retried once before anyone is told (six of these in a row on
// 2026-10-06, all from one page opening five lists together).
const transient = (e) => /Lock was stolen|AbortError|Load failed|Failed to fetch|NetworkError|network/i.test(String((e && (e.message || e.name)) || e || ""));
const range = async (from, to) => {
  let r = await build().range(from, to);
  if (r.error && transient(r.error)) { await new Promise(res => setTimeout(res, 400 + Math.random() * 400)); r = await build().range(from, to); }
  return r;
};
const first = await range(0, PAGE - 1);
if (first.error) {
pmError(code, { raw: first.error, context: "paged fetch: " + label });
return { rows: [], failed: true };
}
const rows = [...(first.data || [])];
if (!first.data || first.data.length < PAGE) return { rows, failed: false };

let next = 1, done = false, failed = false;
while (!done && !failed) {
const batch = [];
for (let k = 0; k < MAX_PARALLEL; k++, next++) {
batch.push(range(next * PAGE, next * PAGE + PAGE - 1));
}
const results = await Promise.all(batch);
for (const r of results) {
if (r.error) {
pmError(code, { raw: r.error, context: "paged fetch: " + label });
failed = true;
break;
}
rows.push(...(r.data || []));
// A short page means the end of the table; anything after it in
// this batch is empty, so stop asking for more.
if (!r.data || r.data.length < PAGE) done = true;
}
}
return { rows, failed };
}
